// Zombie mobs: entity state, a small wander/chase/attack state machine, and a
// spawner tied to night time. Reuses the shared AABB physics.

import { Block } from './blocks';
import { config } from './config';
import { GRAVITY } from './constants';
import { AABBEntity, moveEntity } from './physics';
import { Vec3, vec3 } from './math';
import { mulberry32 } from './noise';
import { Player } from './player';
import { skyState } from './daynight';
import { World } from './world';

export const enum MobState {
  Wander,
  Chase,
  Attack,
}

const MOB_WIDTH = 0.6;
const MOB_HEIGHT = 1.9;
const MOB_HEALTH = 20;
const MOB_SPEED = 3.4;
const SIGHT_RANGE = 16;
const ATTACK_RANGE = 1.6;
const MAX_MOBS = 10;
const DESPAWN_RANGE = 64;

let nextMobId = 1;

export class Zombie implements AABBEntity {
  readonly id = nextMobId++;
  pos: Vec3;
  vel = vec3(0, 0, 0);
  width = MOB_WIDTH;
  height = MOB_HEIGHT;
  onGround = false;

  health = MOB_HEALTH;
  yaw = 0;
  state: MobState = MobState.Wander;
  dead = false;

  /** Visual: seconds since last hit, for the render flash. */
  hurtFlash = 0;
  /** Visual: accumulates while moving, drives the leg swing. */
  walkPhase = 0;

  private wanderDir = vec3(0, 0, 0);
  private wanderTimer = 0;
  private attackTimer = 0;
  private hurtCooldown = 0;

  constructor(pos: Vec3) {
    this.pos = { ...pos };
  }

  update(world: World, player: Player, dt: number): void {
    if (this.dead) return;
    if (this.hurtFlash > 0) this.hurtFlash -= dt;
    if (this.hurtCooldown > 0) this.hurtCooldown -= dt;

    const dx = player.pos.x - this.pos.x;
    const dz = player.pos.z - this.pos.z;
    const distSq = dx * dx + dz * dz;
    const dist = Math.sqrt(distSq);

    // State transitions.
    if (dist <= ATTACK_RANGE) {
      this.state = MobState.Attack;
    } else if (dist <= SIGHT_RANGE) {
      this.state = MobState.Chase;
    } else {
      this.state = MobState.Wander;
    }

    let moveX = 0;
    let moveZ = 0;

    if (this.state === MobState.Chase || this.state === MobState.Attack) {
      if (dist > 0.001) {
        moveX = dx / dist;
        moveZ = dz / dist;
        this.yaw = Math.atan2(-moveX, -moveZ);
      }
      if (this.state === MobState.Attack) {
        this.attackTimer -= dt;
        if (this.attackTimer <= 0) {
          player.damage(config.zombieAttackDamage);
          this.knockback(player, dx, dz, dist);
          this.attackTimer = config.zombieAttackInterval;
        }
        moveX *= 0.3; // ease off pushing into the player while swinging
        moveZ *= 0.3;
      }
    } else {
      this.wander(world, dt);
      moveX = this.wanderDir.x;
      moveZ = this.wanderDir.z;
    }

    const speed = this.state === MobState.Wander ? MOB_SPEED * 0.4 : MOB_SPEED;
    this.vel.x = moveX * speed;
    this.vel.z = moveZ * speed;
    this.vel.y -= GRAVITY * dt;

    // Auto-jump a single-block obstacle when chasing.
    if (this.state === MobState.Chase && this.onGround && (moveX !== 0 || moveZ !== 0)) {
      const aheadX = Math.floor(this.pos.x + Math.sign(moveX) * 0.6);
      const aheadZ = Math.floor(this.pos.z + Math.sign(moveZ) * 0.6);
      if (world.isSolidAt(aheadX, this.pos.y, aheadZ)) {
        this.vel.y = 7.5;
      }
    }

    const moving = moveX !== 0 || moveZ !== 0;
    if (moving) this.walkPhase += dt * 8;

    moveEntity(world, this, dt);
  }

  private wander(world: World, dt: number): void {
    this.wanderTimer -= dt;
    if (this.wanderTimer <= 0) {
      this.wanderTimer = 1 + Math.random() * 3;
      if (Math.random() < 0.4) {
        this.wanderDir = vec3(0, 0, 0); // idle pause
      } else {
        const a = Math.random() * Math.PI * 2;
        this.wanderDir = vec3(Math.cos(a), 0, Math.sin(a));
        this.yaw = Math.atan2(-this.wanderDir.x, -this.wanderDir.z);
      }
    }
    // Avoid walking off a ledge while wandering.
    if ((this.wanderDir.x !== 0 || this.wanderDir.z !== 0) && this.onGround) {
      const fx = Math.floor(this.pos.x + Math.sign(this.wanderDir.x) * 0.6);
      const fz = Math.floor(this.pos.z + Math.sign(this.wanderDir.z) * 0.6);
      if (!world.isSolidAt(fx, this.pos.y - 1, fz)) {
        this.wanderDir = vec3(0, 0, 0);
      }
    }
  }

  private knockback(player: Player, dx: number, dz: number, dist: number): void {
    if (dist < 0.001) return;
    player.vel.x += (dx / dist) * 6;
    player.vel.z += (dz / dist) * 6;
    player.vel.y += 4;
  }

  /** Apply damage from the player. Returns true if this hit killed the mob. */
  hurt(amount: number, fromX: number, fromZ: number): boolean {
    if (this.dead || this.hurtCooldown > 0) return false;
    this.health -= amount;
    this.hurtFlash = 0.25;
    this.hurtCooldown = 0.25;
    // Knock the mob back away from the attacker.
    const dx = this.pos.x - fromX;
    const dz = this.pos.z - fromZ;
    const d = Math.hypot(dx, dz) || 1;
    this.vel.x += (dx / d) * 6;
    this.vel.z += (dz / d) * 6;
    this.vel.y += 4;
    if (this.health <= 0) {
      this.dead = true;
      return true;
    }
    return false;
  }
}

export class MobManager {
  readonly mobs: Zombie[] = [];
  kills = 0;
  private spawnTimer = 0;
  private rand = mulberry32(0x1234abcd);

  update(world: World, player: Player, dt: number): void {
    const sky = skyState(world.time);

    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0) {
      this.spawnTimer = 2;
      if (sky.isNight && this.mobs.length < MAX_MOBS) {
        this.trySpawn(world, player);
      }
    }

    for (const mob of this.mobs) {
      mob.update(world, player, dt);
      // Despawn far away or in daylight.
      const dist = Math.hypot(mob.pos.x - player.pos.x, mob.pos.z - player.pos.z);
      if (dist > DESPAWN_RANGE || (!sky.isNight && dist > 20)) {
        mob.dead = true;
      }
    }

    // Remove dead mobs, tally player kills (health <= 0 means killed).
    for (let i = this.mobs.length - 1; i >= 0; i--) {
      if (this.mobs[i].dead) {
        if (this.mobs[i].health <= 0) this.kills++;
        this.mobs.splice(i, 1);
      }
    }
  }

  private trySpawn(world: World, player: Player): void {
    const angle = this.rand() * Math.PI * 2;
    const radius = 24 + this.rand() * 20;
    const x = Math.floor(player.pos.x + Math.cos(angle) * radius);
    const z = Math.floor(player.pos.z + Math.sin(angle) * radius);
    if (!world.hasChunk(Math.floor(x / 16), Math.floor(z / 16))) return;
    const y = world.surfaceSpawnY(x, z);
    if (y < 1) return;
    // Don't spawn in water or right on top of the player.
    if (world.getBlock(x, y, z) === Block.Water) return;
    this.mobs.push(new Zombie(vec3(x + 0.5, y, z + 0.5)));
  }

  clear(): void {
    this.mobs.length = 0;
  }
}
